import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    ArrowLeft,
    Building2,
    Mail,
    Phone,
    User,
    MapPin,
    CheckCircle2
} from 'lucide-react';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';

export default function InstitutionSignupPage() {
    const [formData, setFormData] = useState({
        institutionName: '',
        institutionType: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        location: '',
        studentCount: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [emailError, setEmailError] = useState('');

    // List of common free email providers to block
    const freeEmailProviders = [
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
        'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
        'zoho.com', 'yandex.com', 'gmx.com', 'live.com',
        'msn.com', 'me.com', 'inbox.com', 'fastmail.com'
    ];

    const validateWorkEmail = (email: string): boolean => {
        if (!email) return false;
        
        const emailLower = email.toLowerCase();
        const domain = emailLower.split('@')[1];
        
        if (!domain) return false;
        
        // Check if it's a free email provider
        if (freeEmailProviders.includes(domain)) {
            setEmailError('Please use your institutional/work email address. Personal email addresses (Gmail, Yahoo, etc.) are not accepted.');
            return false;
        }
        
        // Check for common institutional email patterns
        const institutionalPatterns = [
            /\.edu$/,           // .edu domains
            /\.edu\.[a-z]{2}$/, // .edu.ng, .edu.gh, etc.
            /\.ac\.[a-z]{2}$/,  // .ac.uk, .ac.za, etc.
            /\.sch\.[a-z]{2}$/, // .sch.ng, etc.
        ];
        
        const hasInstitutionalPattern = institutionalPatterns.some(pattern => pattern.test(domain));
        
        if (!hasInstitutionalPattern) {
            setEmailError('Please use your institutional email address (e.g., name@university.edu.ng). Work emails from recognized institutions are required.');
            return false;
        }
        
        setEmailError('');
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate work email before submission
        if (!validateWorkEmail(formData.contactEmail)) {
            return;
        }
        
        // In production, this would send to the backend
        console.log('Institution signup:', formData);
        setSubmitted(true);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // Clear email error when user starts typing again
        if (name === 'contactEmail' && emailError) {
            setEmailError('');
        }
    };

    const handleEmailBlur = () => {
        if (formData.contactEmail) {
            validateWorkEmail(formData.contactEmail);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#FDF8F6] flex flex-col">
                <UniversalHeader />
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-md w-full text-center space-y-6">
                        <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle2 className="h-10 w-10 text-green-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900">Request Received</h1>
                        <p className="text-slate-600">
                            Thank you for your interest in CareVault Campus. Our team will contact you within 2 business days to schedule a demo and discuss implementation.
                        </p>
                        <Button asChild className="bg-[#D64463] hover:bg-[#b0324d]">
                            <Link to="/">Return to Homepage</Link>
                        </Button>
                    </div>
                </div>
                <UniversalFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDF8F6]">
            <UniversalHeader />

            <div className="container mx-auto px-4 py-12">
                <div className="max-w-2xl mx-auto">
                    {/* Back Link */}
                    <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#D64463] mb-8">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Homepage
                    </Link>

                    {/* Header */}
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D64463]/10 text-[#D64463] font-semibold text-sm mb-4">
                            <Building2 className="h-4 w-4" />
                            Institution Registration
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                            Bring CareVault to Your Campus
                        </h1>
                        <p className="text-lg text-slate-600 max-w-xl mx-auto">
                            Fill out this form and our team will contact you to schedule a demo and discuss your institution's needs.
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-8 space-y-6">
                        {/* Institution Details */}
                        <div className="space-y-4">
                            <h2 className="text-lg font-bold text-slate-900 border-b pb-2">Institution Details</h2>

                            <div>
                                <Label htmlFor="institutionName">Institution Name</Label>
                                <div className="relative mt-1">
                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <Input
                                        id="institutionName"
                                        name="institutionName"
                                        placeholder="University of Lagos"
                                        value={formData.institutionName}
                                        onChange={handleChange}
                                        className="pl-10"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="institutionType">Institution Type</Label>
                                    <select
                                        id="institutionType"
                                        name="institutionType"
                                        value={formData.institutionType}
                                        onChange={handleChange}
                                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                        required
                                    >
                                        <option value="">Select type...</option>
                                        <option value="university">University</option>
                                        <option value="polytechnic">Polytechnic</option>
                                        <option value="college">College of Education</option>
                                        <option value="other">Other Tertiary Institution</option>
                                    </select>
                                </div>

                                <div>
                                    <Label htmlFor="studentCount">Approximate Student Population</Label>
                                    <select
                                        id="studentCount"
                                        name="studentCount"
                                        value={formData.studentCount}
                                        onChange={handleChange}
                                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                        required
                                    >
                                        <option value="">Select range...</option>
                                        <option value="small">Under 5,000</option>
                                        <option value="medium">5,000 - 20,000</option>
                                        <option value="large">20,000 - 50,000</option>
                                        <option value="xlarge">Over 50,000</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="location">Location (City, State)</Label>
                                <div className="relative mt-1">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <Input
                                        id="location"
                                        name="location"
                                        placeholder="Akoka, Lagos"
                                        value={formData.location}
                                        onChange={handleChange}
                                        className="pl-10"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contact Details */}
                        <div className="space-y-4 pt-4">
                            <h2 className="text-lg font-bold text-slate-900 border-b pb-2">Contact Person</h2>

                            <div>
                                <Label htmlFor="contactName">Full Name</Label>
                                <div className="relative mt-1">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <Input
                                        id="contactName"
                                        name="contactName"
                                        placeholder="Dr. Amina Ibrahim"
                                        value={formData.contactName}
                                        onChange={handleChange}
                                        className="pl-10"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="contactEmail">Work Email Address *</Label>
                                    <div className="relative mt-1">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <Input
                                            id="contactEmail"
                                            name="contactEmail"
                                            type="email"
                                            placeholder="name@university.edu.ng"
                                            value={formData.contactEmail}
                                            onChange={handleChange}
                                            onBlur={handleEmailBlur}
                                            className={`pl-10 ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                                            required
                                        />
                                    </div>
                                    {emailError && (
                                        <p className="text-sm text-red-600 mt-1 flex items-start gap-1">
                                            <span className="text-red-600 font-bold">⚠</span>
                                            <span>{emailError}</span>
                                        </p>
                                    )}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Institutional email required (.edu, .ac, etc.)
                                    </p>
                                </div>

                                <div>
                                    <Label htmlFor="contactPhone">Phone Number</Label>
                                    <div className="relative mt-1">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <Input
                                            id="contactPhone"
                                            name="contactPhone"
                                            type="tel"
                                            placeholder="+234 801 234 5678"
                                            value={formData.contactPhone}
                                            onChange={handleChange}
                                            className="pl-10"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="pt-6">
                            <Button type="submit" className="w-full h-12 bg-[#D64463] hover:bg-[#b0324d] text-lg font-bold rounded-full">
                                Request a Demo
                            </Button>
                            <p className="text-center text-sm text-slate-500 mt-4">
                                By submitting, you agree to our Terms of Service and Privacy Policy.
                            </p>
                        </div>
                    </form>
                </div>
            </div>

            <UniversalFooter />
        </div>
    );
}
